import { useCallback, useEffect, useRef, useState } from 'react';
import type { AggregatedDish } from '../types';
import { formatWaitTime } from '../utils/time';
import { AllergenBadge } from './AllergenBadge';

interface Props {
  dish: AggregatedDish;
  onDone: (itemId: string) => void;
  onDoneAll?: (itemId: string, remaining: number) => void;
  readOnly?: boolean;
  editingOrderIds?: ReadonlySet<string>;
  editingOrderLabels?: ReadonlyMap<string, string>;
  /** i18n translate function — needed for allergen labels */
  t?: (key: string) => string;
}

export function KdsItemCard({ dish, onDone, onDoneAll, readOnly, editingOrderIds, editingOrderLabels, t }: Props) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  // Cooking time: from earliest started_at (all items are InProgress)
  const earliestStart = dish.entries.reduce<number | null>(
    (earliest, e) => e.started_at && (earliest === null || e.started_at < earliest) ? e.started_at : earliest,
    null,
  );
  const cookingSeconds = earliestStart ? Math.floor((now - earliestStart) / 1000) : 0;

  return (
    <div className="rounded-lg border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm overflow-hidden">
      {/* Header: dish name + total quantity + time */}
      <div className="px-3 py-2 flex items-center justify-between gap-2 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          {/* 临时菜品立宪 2026-08-01 §2.6:临时行没有商品编号,叫号位整块不渲染 ——
              留一个空的黑徽章比不渲染更糟,厨师会以为编号丢了。 */}
          {dish.external_id != null && (
            <span className="text-xs font-bold font-mono text-white bg-gray-900/85 px-2 py-0.5 rounded shrink-0">
              {dish.external_id}
            </span>
          )}
          <h3 className="text-base font-bold text-slate-900">
            {dish.kitchen_name}
          </h3>
          <span className="text-lg font-bold text-primary-500 shrink-0">
            x{dish.total_remaining}
          </span>
        </div>
        <span className="text-sm font-mono font-bold tabular-nums text-blue-600 shrink-0">
          {formatWaitTime(cookingSeconds)}
        </span>
      </div>

      {/* Per-entry breakdown: each item's details — tap +1, long-press all done */}
      <div className="divide-y divide-slate-100">
        {dish.entries.map((entry) => {
          const shortReceipt = entry.receipt_number.split('-').pop();
          const label = entry.table_name ?? `#${shortReceipt}`;
          const isEditing = editingOrderIds?.has(entry.order_id) ?? false;
          const hasDetails = entry.spec_name || entry.options.length > 0 || entry.note || entry.guest_allergens.length > 0;

          return (
            <EntryRow
              key={entry.item_id}
              itemId={entry.item_id}
              remaining={entry.remaining}
              readOnly={readOnly}
              onDone={onDone}
              onDoneAll={onDoneAll}
            >
              {/* Row 1: order label + quantity + spec + time */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium shrink-0">{label}</span>
                {isEditing && (
                  <span className="text-xs font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                    {editingOrderLabels?.get(entry.order_id) ?? t?.('kds.orderEditing') ?? '编辑中'}
                  </span>
                )}
                {entry.remaining > 1 && (
                  <span className="text-xs font-bold text-primary-500">x{entry.remaining}</span>
                )}
                {entry.spec_name && (
                  <span className="text-xs font-bold text-violet-800 bg-violet-100 px-1.5 py-0.5 rounded">
                    {entry.spec_name}
                  </span>
                )}
                {entry.started_at && (
                  <span className="ml-auto text-xs font-mono tabular-nums text-blue-500 shrink-0">
                    {formatWaitTime(Math.floor((now - entry.started_at) / 1000))}
                  </span>
                )}
              </div>
              {/* Row 2: options + note */}
              {hasDetails && (
                <div className="mt-0.5 flex flex-col gap-0.5">
                  {entry.options.map((opt, i) => {
                    const colonIdx = opt.indexOf(':');
                    if (colonIdx > 0) {
                      return (
                        <span key={i} className="text-xs text-slate-600">
                          <span className="font-bold text-slate-800">{opt.slice(0, colonIdx)}:</span>{opt.slice(colonIdx + 1)}
                        </span>
                      );
                    }
                    return <span key={i} className="text-xs text-slate-600">{opt}</span>;
                  })}
                  {entry.guest_allergens.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {entry.guest_allergens.map((a, i) => {
                        const isCollision = entry.product_allergens.includes(a);
                        return (
                          <AllergenBadge key={i} allergenKey={a} t={t} variant={isCollision ? 'collision' : 'guest'} />
                        );
                      })}
                    </div>
                  )}
                  {entry.note && (
                    <span className="text-xs text-amber-600">{entry.note}</span>
                  )}
                </div>
              )}
            </EntryRow>
          );
        })}
      </div>
    </div>
  );
}

// ── Entry row — tap +1, long-press all done (same pattern as TicketCard ItemRow) ──

function EntryRow({
  itemId, remaining, readOnly, onDone, onDoneAll, children,
}: {
  itemId: string; remaining: number; readOnly?: boolean;
  onDone: (id: string) => void;
  onDoneAll?: (id: string, remaining: number) => void;
  children: React.ReactNode;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);
  const [pressing, setPressing] = useState(false);

  const isActive = !readOnly;

  const handleStart = useCallback((e: React.PointerEvent) => {
    if (!isActive) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    firedRef.current = false;
    setPressing(true);
    if (onDoneAll) {
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        setPressing(false);
        onDoneAll(itemId, remaining);
      }, 500);
    }
  }, [itemId, remaining, isActive, onDoneAll]);

  const handleEnd = useCallback((e: React.PointerEvent) => {
    if (!isActive) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setPressing(false);
    if (!firedRef.current) onDone(itemId);
  }, [itemId, isActive, onDone]);

  const handleCancel = useCallback((e: React.PointerEvent) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    setPressing(false);
  }, []);

  return (
    <div
      onPointerDown={handleStart}
      onPointerUp={handleEnd}
      onPointerCancel={handleCancel}
      onContextMenu={(e) => { if (isActive) e.preventDefault(); }}
      className={`flex min-h-[var(--size-touch-large)] items-center px-3 touch-manipulation select-none ${
        isActive ? 'cursor-pointer hover:bg-blue-50 active:bg-blue-100' : ''
      } ${pressing ? '!bg-blue-100' : ''}`}
    >
      {children}
    </div>
  );
}
